import React, { useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";

import FormField from "../../../../components/form-field/FormField";
import { NameEntity } from "../../../../helpers/NameEntity";
import SuggestionBox from "../../../../components/suggestion-box/SuggestionBox";
import { translationService } from "../../../../services/translation/TranslationService";
import {
  DataSection,
  SubSection,
} from "../../../../components/data-components/sections/DataSections";
import { UrlParams } from "../../../../components/datatable/interfaces";
import { RequiredObjectValidatorModel } from "../../../../services/form/validators/required/RequiredObjectValidator";
import { GisCoordinatesStorageModel } from "../../../../services/organization/GisCoordinatesStorage";
import { api } from "../../../../services/api/ApiService";
import { CoordinateSystem, Description } from "../../../../services/organization/GisCoordinates";

interface DisplayCoordinatesSystemProps {
  coordinateSystem: CoordinateSystem;
}

export function DisplayCoordinatesSystem(props: DisplayCoordinatesSystemProps) {
  return (
    <div className="gis-coordinates-section">
      <SubSection>
        <FormField
          id="coordinate-system-name"
          labelKey="gis-coordinates.DisplayGisCoordinatesName.label"
          readOnly
          fieldValue={props.coordinateSystem?.name || "-"}
        />
      </SubSection>
      <SubSection>
        <FormField
          id="coordinate-system-wkid"
          labelKey="gis-coordinates.DisplayGisCoordinatesWKID.label"
          readOnly
          fieldValue={props.coordinateSystem?.wkid || "-"}
        />
      </SubSection>
    </div>
  );
}

interface DisplayCoordinateSystemDescriptionProps {
  gisCoordinateDescription: Description | undefined;
}

export const DisplayCoordinateSystemDescription = observer(
  (props: DisplayCoordinateSystemDescriptionProps) => {
    const gisCoordinateDescription = props.gisCoordinateDescription;
    return (
      <DataSection labelKey="general.description">
        <div className="description-fields">
          <div>
            {translationService.translate("general.gis-coordinates.area-of-use")}:{" "}
            {gisCoordinateDescription?.areaOfUse ? gisCoordinateDescription.areaOfUse : "-"}
          </div>
          <div>
            {translationService.translate("general.gis-coordinates.min-latitude")}:{" "}
            {gisCoordinateDescription?.minimumLatitude
              ? gisCoordinateDescription.minimumLatitude
              : "-"}
          </div>
          <div>
            {translationService.translate("general.gis-coordinates.min-longitude")}:{" "}
            {gisCoordinateDescription?.minimumLongitude
              ? gisCoordinateDescription.minimumLongitude
              : "-"}
          </div>
          <div>
            {translationService.translate("general.gis-coordinates.max-latitude")}:{" "}
            {gisCoordinateDescription?.maximumLatitude
              ? gisCoordinateDescription.maximumLatitude
              : "-"}
          </div>
          <div>
            {translationService.translate("general.gis-coordinates.max-longitude")}:{" "}
            {gisCoordinateDescription?.maximumLongitude
              ? gisCoordinateDescription.maximumLongitude
              : "-"}
          </div>
        </div>
      </DataSection>
    );
  }
);

const mapCoordinateSystemsNameToUIObject = (coordinateSystems: CoordinateSystem[]) => {
  return coordinateSystems.map((c) => {
    return { id: c.id, name: c.name, key: c.id };
  });
};

const mapCoordinateSystemsWKIDToUIObject = (coordinateSystems: CoordinateSystem[]) => {
  return coordinateSystems.map((c) => {
    return { id: c.id, name: c.wkid, key: c.id };
  });
};

interface EditCoordinateSystemProps {
  name: NameEntity;
  WKID: NameEntity;
  setFieldValue: Function;
  areFieldsRequired: boolean;
}

export const EditCoordinateSystem = observer((props: EditCoordinateSystemProps) => {
  const [requiredObjectValidator] = useState(() => RequiredObjectValidatorModel.create({}));
  const [gisCoordinateStorage] = useState(() =>
    GisCoordinatesStorageModel.create({ autoLoad: false }, { api })
  );

  useEffect(() => {
    async function loadCoordinateSystems() {
      const nameQueryParam: UrlParams = { name: "" };
      await gisCoordinateStorage.loadAvailableGisCoordinates(nameQueryParam);
    }

    loadCoordinateSystems();
  }, [gisCoordinateStorage]);

  const { setFieldValue } = props;

  const handleSearchCoordinateSystemNameChange = useCallback(
    async (searchText: string) => {
      const filteredOptions = gisCoordinateStorage.availableGisCoordinates.filter((x) =>
        x.name.toLowerCase().match(searchText.toLowerCase())
      );
      return Promise.resolve(mapCoordinateSystemsNameToUIObject(filteredOptions));
    },
    [gisCoordinateStorage]
  );

  const handleSearchCoordinateSystemWKIDChange = useCallback(
    async (searchText: string) => {
      const filteredOptions = gisCoordinateStorage.availableGisCoordinates.filter((x) =>
        x.wkid.toLowerCase().match(searchText.toLowerCase())
      );
      return Promise.resolve(mapCoordinateSystemsWKIDToUIObject(filteredOptions));
    },
    [gisCoordinateStorage]
  );

  const changeCoordinateSystemName = useCallback(
    (coordinateSystem: NameEntity | undefined) => {
      const id: number | null = coordinateSystem ? (coordinateSystem.id as number) : null;
      const name = coordinateSystem ? coordinateSystem.name : null;
      setFieldValue("gisCoordinatesId", id);
      setFieldValue("coordinateSystemName", {
        id,
        name,
      });
      setFieldValue("coordinateSystemWKID", {
        id,
        name: gisCoordinateStorage.availableGisCoordinates.find((x) => x.id === id)?.wkid,
      });
      setFieldValue(
        "gisCoordinateDescription",
        gisCoordinateStorage.availableGisCoordinates.find((x) => x.id === id)?.description
      );
    },
    [gisCoordinateStorage.availableGisCoordinates, setFieldValue]
  );

  const changeCoordinateSystemWKID = useCallback(
    (coordinateSystemWKID: NameEntity | undefined) => {
      const id: number | null = coordinateSystemWKID ? (coordinateSystemWKID.id as number) : null;
      const name = coordinateSystemWKID ? coordinateSystemWKID.name : null;
      setFieldValue("gisCoordinatesId", id);
      setFieldValue("coordinateSystemWKID", {
        id,
        name,
      });
      setFieldValue("coordinateSystemName", {
        id: id,
        name: gisCoordinateStorage.availableGisCoordinates.find((x) => x.id === id)?.name,
      });
      setFieldValue(
        "gisCoordinateDescription",
        gisCoordinateStorage.availableGisCoordinates.find((x) => x.id === id)?.description
      );
    },
    [gisCoordinateStorage.availableGisCoordinates, setFieldValue]
  );

  const clearCoordinateSystemFieldValue = (text: string) => {
    if (!text) {
      setFieldValue("gisCoordinatesId", null);
      setFieldValue("coordinateSystemName", {});
      setFieldValue("coordinateSystemWKID", {});
    }
  };

  const clearWkidFieldValue = (text: string) => {
    if (!text) {
      setFieldValue("gisCoordinatesId", null);
      setFieldValue("coordinateSystemName", {});
      setFieldValue("coordinateSystemWKID", {});
    }
  };

  return (
    <div className="coordinate-system-edit">
      {gisCoordinateStorage.areGisCoordinatesLoading ? (
        <> </>
      ) : (
        <DataSection labelKey="general.coordinate-system">
          <SubSection>
            <FormField
              id="coordinate-system-name"
              labelKey="gis-coordinates.EditGisCoordinatesName.label"
              fieldName="coordinateSystemName"
              fieldValue={props.name ? props.name.id : null}
              validator={props.areFieldsRequired ? requiredObjectValidator : undefined}
              required={props.areFieldsRequired}
              children={() => (
                <SuggestionBox
                  defaultSelectedItem={props.name ? props.name : undefined}
                  initialOptions={mapCoordinateSystemsNameToUIObject(
                    gisCoordinateStorage.availableGisCoordinates
                  )}
                  onSearchTextChange={(searchText) =>
                    handleSearchCoordinateSystemNameChange(searchText)
                  }
                  searchTextChanged={clearCoordinateSystemFieldValue}
                  selectedItemChanged={changeCoordinateSystemName}
                  placeholderKey="gis-coordinates.EditCoordinateSystemName.suggestion-placeholder"
                  noResultsFoundKey="general.gis-coordinates.not-found"
                  clearCurrentItem={() => changeCoordinateSystemName(undefined)}
                />
              )}
              errorName="coordinateSystemName"
            />
          </SubSection>
          <SubSection>
            <FormField
              id="coordinate-system-wkid"
              labelKey="gis-coordinates.EditGisCoordinatesWKID.label"
              fieldName="coordinateSystemWKID"
              fieldValue={props.WKID ? props.WKID.id : null}
              validator={props.areFieldsRequired ? requiredObjectValidator : undefined}
              required={props.areFieldsRequired}
              children={() => (
                <SuggestionBox
                  defaultSelectedItem={props.WKID ? props.WKID : undefined}
                  initialOptions={mapCoordinateSystemsWKIDToUIObject(
                    gisCoordinateStorage.availableGisCoordinates
                  )}
                  onSearchTextChange={(searchText) =>
                    handleSearchCoordinateSystemWKIDChange(searchText)
                  }
                  searchTextChanged={clearWkidFieldValue}
                  selectedItemChanged={changeCoordinateSystemWKID}
                  placeholderKey="gis-coordinates.EditCoordinateSystemWKID.suggestion-placeholder"
                  noResultsFoundKey="general.gis-coordinates.not-found"
                  clearCurrentItem={() => changeCoordinateSystemWKID(undefined)}
                />
              )}
              errorName="coordinateSystemWKID"
            />
          </SubSection>
        </DataSection>
      )}
    </div>
  );
});
